import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { withTranslation, Trans } from 'react-i18next';

import { makeGetTheme } from '../selectors/app-props';
import { makeGetCurrentUser } from '../selectors/users';
import { makeGetSortedActiveTeams, makeGetCurrentTeam } from '../selectors/teams';
import { makeGetPanelNotificationsCount } from '../selectors/notifications';

import { updateCurrentUser } from '../actions/users';
import { showModal } from '../actions/modal';
import { openFlyout } from '../actions/flyout';

import config from '../config';
import { trackEvent } from '../services/events-tracker-service';
import { featureAvailable } from '../services/features-service';
import { getSessionLength, logout, switchTeam, openIntercom } from '../globals-service';

import CanUser from './can-user';
import Popout from './popout';
import AvatarBase from './avatar-base';
import Tooltip from './tooltip';
import Toggle from './toggle';
import Select from './select';
import TeamsList from 'components/TeamsList';

import IconUpload from '../icons/icon-upload';
import IconPlus from '../icons/icon-plus';
import IconArrowDown from '../icons/icon-arrow-down';
import IconSearch from '../icons/icon-search';
import IconSun from '../icons/icon-sun';
import IconLink from '../icons/icon-link';

class SettingsCompact extends Component {
    state = {
        popoutVisible: false
    };

    handleAccountSettingsClick = () => {
        this.props.history.push({
            pathname: '/settings/profile',
            state: {
                closeUrl: this.props.location.pathname
            }
        });

        this.closePopout();
    }

    handleAppCenterClick = () => {
        this.props.history.push({
            pathname: '/settings/apps',
            state: {
                closeUrl: this.props.location.pathname
            }
        });

        this.closePopout();
    }

    handleToggleClick = () => {
        if (this.state.popoutVisible) {
            this.closePopout();
        } else {
            this.openPopout();
        }
    }

    doLogout = () => {
        trackEvent('USER.LOGOUT', {
            sessionLength: getSessionLength() / 1000 / 60
        }).then(logout);
    }

    handlePopoutShow = () => {
        this.setState({
            popoutVisible: true
        });
    }

    handlePopoutHide = () => {
        this.setState({
            popoutVisible: false
        });
    }

    handleShootUsMessageClick = () => {
        openIntercom();
    }

    handleImportProjectsClick = () => {
        showModal('IMPORT_PROJECTS');

        this.closePopout();
    }

    handleCreateTeamClick = () => {
        showModal('CREATE_TEAM');

        this.closePopout();
    }

    handleThemeChange = ({ id: theme }) => {
        updateCurrentUser({
            theme,
            team_id: this.props.team.id
        });
    }

    handleTeamEdit = () => {
        this.closePopout();
    }

    handleTeamSelect = () => {
        this.closePopout();
    }

    openPopout() {
        this.popoutCtrl.show();
    }

    closePopout() {
        this.popoutCtrl.hide();
    }

    get themeOptions() {
        const { t } = this.props;

        return [
            {
                id: 'light',
                text: t('themes.labels.classic')
            },
            {
                id: 'full_light',
                text: t('themes.labels.light')
            },
            {
                id: 'dark',
                text: t('themes.labels.dark')
            }
        ];
    }

    render() {
        if (!this.props.team) {
            return <div className="settings-compact" />
        }

        const { t } = this.props;

        return (
            <div className={'settings-compact' + (this.state.popoutVisible ? ' popout-visible' : '')}>
                <div className="settings-compact-main" onClick={this.handleToggleClick}>
                    <div className="image">
                        <AvatarBase size="small" text={this.props.team.initials} image={this.props.team.logo} color={this.props.team.color} foregroundColor={this.props.team.secondary_color} />
                    </div>

                    <div className="settings-compact-content">
                        <span className="title">{this.props.team.name}</span>

                        <span className="arrow">
                            <IconArrowDown />
                        </span>
                    </div>
                </div>

                <Popout
                    className="no-arrow dark settings-compact-popout"
                    xAlign="left"
                    resizeToFit={true}
                    provideController={ctrl => {this.popoutCtrl = ctrl}}
                    onShow={this.handlePopoutShow}
                    onHide={this.handlePopoutHide}
                >
                    <div className="popout-main">
                        <div className="popout-section">
                            <div className="section-head">
                                <h4>{t('Profile')}</h4>
                            </div>

                            <div className="section-body popout-section-profile">
                                {
                                    this.props.currentUser ? (
                                        <div className="inline-nav-item current">
                                            <div className="item-head">
                                                <AvatarBase image={this.props.currentUser.avatar} color={this.props.currentUser.color} foregroundColor={this.props.currentUser.secondary_color} text={this.props.currentUser.initials} />
                                            </div>

                                            <div className="item-body">
                                                {this.props.currentUser.name}
                                            </div>

                                            <div className="item-foot">
                                                <button className="btn" onClick={this.handleAccountSettingsClick}>
                                                    <i className="ion ion-ios-gear" />
                                                </button>
                                            </div>
                                        </div>
                                    ) : null
                                }
                            </div>
                        </div>

                        <div className="popout-section">
                            <div className="section-head">
                                <h4>{t('Workspaces')}</h4>
                            </div>

                            <div className="section-body popout-section-teams">
                                <TeamsList showDirectTeams={true} withSwitch={true} withEdit={true} onTeamSelect={this.handleTeamSelect} onEditTeam={this.handleTeamEdit} />
                            </div>
                        </div>

                        {
                            (this.props.linkedTeams || []).length ? (
                                <div className="popout-section">
                                    <div className="section-head">
                                        <h4>{ t('Linked Workspaces') }:</h4>
                                    </div>

                                    <div className="section-body popout-section-teams">
                                        <TeamsList showLinkedTeams={true} withSwitch={true} onTeamSelect={this.handleTeamSelect} />
                                    </div>
                                </div>
                            ) : null
                        }

                        <div className="popout-section popout-section-actions">
                            <div className="actions-group">
                                <div className="action-item-slim">
                                    <div className="action-item-icon">
                                        <IconSun />
                                    </div>

                                    <div className="action-item-body">
                                        { t('themes.changeLabel') }
                                    </div>

                                    <div className="action-item-foot">
                                        <Select
                                            small={true}
                                            selected={this.props.theme}
                                            options={this.themeOptions}
                                            onChange={this.handleThemeChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="popout-section popout-section-actions">
                            <div className="actions-group">
                                <button className="btn btn-simple btn-small large-text" onClick={this.handleAppCenterClick}>
                                    <i className="icon material-icons" style={{ color: '#00a99b', fontSize: 15 }}>apps</i>

                                    { t('apps:App Center') }
                                </button>
                            </div>

                            <CanUser type="createProject">
                                <div className="actions-group">
                                    {
                                        !featureAvailable('import') ? (
                                            <Tooltip tooltip={t('billing:upgrade.import')}>
                                                <div style={{ width: '100%' }}>
                                                    <button className="btn btn-simple btn-small large-text muted" style={{ width: '100%' }} disabled>
                                                        <IconUpload color="#00a99b" size="14px" />

                                                        {t('import:Import Projects')}
                                                    </button>
                                                </div>
                                            </Tooltip>
                                        ) : (
                                            <button className="btn btn-simple btn-small large-text" onClick={this.handleImportProjectsClick}>
                                                <IconUpload color="#00a99b" size="14px" />

                                                {t('import:Import Projects')}
                                            </button>
                                        )
                                    }
                                </div>
                            </CanUser>

                            <div className="actions-group">
                                <button className="btn btn-simple btn-small large-text" onClick={this.handleAccountSettingsClick}>
                                    <IconLink color="#00a99b" size="12px" />

                                    {t('team:Connect Workspace')}
                                </button>
                            </div>

                            <div className="actions-group">
                                <button className="btn btn-simple btn-small large-text" onClick={this.handleCreateTeamClick}>
                                    <IconPlus color="#00a99b" size="12px" />

                                    {t('team:Create New Workspace')}
                                </button>
                            </div>

                            <div className="actions-group">
                                <a className="btn btn-simple btn-small large-text" href="https://changelog.niftypm.com/" target="_blank" rel="noopener noreferrer">
                                    <span role="img" aria-label="stars">✨</span>&nbsp;
                                    {t('What’s new')}
                                </a>

                                <button className="btn btn-simple btn-small large-text" onClick={this.doLogout}>
                                    <i className="icon material-icons">power_settings_new</i>

                                    {t('Sign Out')}
                                </button>
                            </div>
                        </div>

                        <div className="popout-section help-ad">
                            <div className="help-ad-icon">
                                <i className="ico-lego-head" />
                            </div>

                            <div className="help-ad-body">
                                <h5>{t('help.title')}</h5>

                                {
                                    config.BILLING_ENABLED ? (
                                        <p>
                                            <Trans
                                                i18nKey="settings:help.bodyWithTutorials"
                                                components={{
                                                    contact: <a onClick={this.handleShootUsMessageClick} />,
                                                    tutorialsLink: <a href="http://help.niftypm.com" target="_blank" rel="noopener noreferrer" />
                                                }}
                                            />
                                        </p>
                                    ) : (
                                        <p>
                                            <Trans
                                                i18nKey="settings:help.body"
                                                components={{
                                                    contact: <a onClick={this.handleShootUsMessageClick} />
                                                }}
                                            />
                                        </p>
                                    )
                                }

                            </div>
                        </div>
                    </div>
                </Popout>
            </div>
        );
    }
}

const makeMapStateToProps = () => {
    const getCurrentUser = makeGetCurrentUser();
    const getCurrentTeam = makeGetCurrentTeam();
    const getSortedActiveTeams = makeGetSortedActiveTeams();
    const getPanelNotificationsCount = makeGetPanelNotificationsCount();
    const getTheme = makeGetTheme();

    return ({ teams, users, notifications, appProps }, ownProps) => {
        const team = getCurrentTeam(teams);
        return {
            team,
            teams: getSortedActiveTeams(teams.items),
            linkedTeams: teams.linkedTeams,
            currentUser: getCurrentUser(users),
            notificationsCount: getPanelNotificationsCount(notifications.items),
            theme: getTheme(appProps)
        }
    };
}

export default withRouter(connect(makeMapStateToProps)(withTranslation(['settings', 'common', 'billing', 'import', 'onboarding', 'team', 'notifications', 'apps'])(SettingsCompact)));
